import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { COMMUNITY_PROFILES, type CommunityProfile } from "@/data/communityProfiles";

const CommunityCard = ({ profile }: { profile: CommunityProfile }) => {
  const { data: counts } = useQuery({
    queryKey: ["insights-community-counts", profile.slug],
    queryFn: async () => {
      const kw = profile.match.keywords[0] ?? profile.name.toLowerCase();
      const [legis, cases] = await Promise.all([
        supabase
          .from("legislation")
          .select("id", { count: "exact", head: true })
          .in("county", profile.match.counties),
        supabase.rpc("get_public_case_studies"),
      ]);
      const caseCount = (cases.data ?? []).filter((c: any) => {
        const hay = `${c.idea_title ?? ""} ${c.idea_description ?? ""} ${(c.tags ?? []).join(" ")}`.toLowerCase();
        return profile.match.keywords.some((k) => hay.includes(k));
      }).length;
      return {
        legislation: legis.count ?? 0,
        caseStudies: caseCount,
        keyword: kw,
      };
    },
  });

  return (
    <Link
      to={`/communities/${profile.slug}`}
      className="group block bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors min-w-[280px] flex-1"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-primary mb-3">
        <MapPin className="h-3.5 w-3.5" />
        <span className="uppercase tracking-wider">
          {profile.kind === "county" ? "County" : `City · ${profile.parent}`}
        </span>
      </div>
      <h3 className="font-display text-xl font-bold mb-2 group-hover:text-primary transition-colors">
        {profile.name}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-3">
        {profile.tagline}
      </p>
      <div className="flex items-center gap-4 text-xs text-muted-foreground border-t border-border pt-4">
        <span>
          <span className="font-semibold text-foreground">{counts?.legislation ?? "—"}</span> bills
        </span>
        <span>
          <span className="font-semibold text-foreground">{counts?.caseStudies ?? "—"}</span> case studies
        </span>
        <span className="ml-auto inline-flex items-center text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Open <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
};

const CommunityInsightsStrip = () => {
  return (
    <section className="py-16 border-t border-border/60">
      <div className="container mx-auto px-6">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Places</span>
            <h2 className="font-display text-3xl font-bold mt-2">Insights by Community</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Every story, bill, and case study below is rooted in a specific Alabama place. Start with one.
            </p>
          </div>
          <Link
            to="/communities"
            className="text-sm text-primary font-semibold inline-flex items-center group"
          >
            All community profiles
            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid sm:grid-cols-2 gap-5"
        >
          {COMMUNITY_PROFILES.map((p) => (
            <CommunityCard key={p.slug} profile={p} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CommunityInsightsStrip;